/**
 * How tall a base cabinet is, and how deep a scanned run's boxes are.
 *
 * A leaf module on purpose: `scanRuns` (which derives a run's span),
 * `materializeScanRuns` and `materializeRun` (which build its bays) and
 * `runEndClosure` (which decides whether the span reaches a corner) all need
 * the same answers, and routing them through each other would make a require
 * cycle. Its requires are `../kitchen-assembly/constants` and `./profiles`,
 * neither of which requires anything in this subsystem, so the leaf stays a
 * leaf. (`profiles` is here because one rule below is a KITCHEN rule and has
 * to say so — see `applianceDepthFloor`.)
 *
 * Pure. No I/O.
 */

const { KITCHEN_CABINET_DIMENSIONS } = require('../kitchen-assembly/constants');
const { profileOf, VANITY_PROFILE_ID } = require('./profiles');

const IN = 0.0254;

const STANDARD_CABINET_DEPTH = KITCHEN_CABINET_DIMENSIONS.depth;

// A run BAY is a base-height cabinet. Post-processing also emits `lower-cabinet`
// for units that are not bays — a floor-to-ceiling pantry (rule 3), a
// custom-height unit built to a scanned storage's own height (rule 4).
const BASE_HEIGHT = 34.5 * IN;
const BASE_HEIGHT_TOLERANCE = 6 * IN;

function isBaseHeight(o) {
  const h = o?.dimensions?.y;
  if (typeof h !== 'number') return false;
  return Math.abs(h - BASE_HEIGHT) <= BASE_HEIGHT_TOLERANCE;
}

/**
 * The depth the scan actually measured for these cabinet bodies: the median
 * depth of the base-height ones, or null when none of them says.
 *
 * Median rather than mean so one mis-measured box cannot drag the whole run,
 * and base-height only because a pantry's depth is not the run's bay depth.
 * Callers that BUILD at this depth may raise it further (see
 * `materializeScanRuns`, which floors it at a housed appliance); callers that
 * merely ask "how deep is this run" want it raw.
 */
function measuredCabinetDepth(bodies) {
  const depths = (bodies || [])
    .filter(isBaseHeight)
    .map((o) => o?.dimensions?.z)
    .filter((z) => typeof z === 'number' && z > 0)
    .sort((a, b) => a - b);
  if (!depths.length) return null;
  return depths[Math.floor(depths.length / 2)];
}

/**
 * The depth to build this run's bays at: the median depth of the cabinets the
 * scan actually measured there.
 *
 * Not the engine's standard 24". Post-processing works hard to derive a real
 * depth per cabinet — a shallow 15.75" run against a pinched corridor, a
 * back-flush correction — and rebuilding at the standard silently discards it
 * (that exact case is pinned by a unit test). Median rather than mean so one
 * mis-measured box cannot drag the whole run.
 */
function runCabinetDepth(bodies, run) {
  const measured = measuredCabinetDepth(bodies);
  if (measured == null) return null;
  return Math.max(measured, applianceDepthFloor(run, measured));
}

// A housed appliance can pull its run no further than this. Beyond it the run
// is not a mis-measured standard one — it is a different run, and the
// appliance is more likely mis-assigned than the whole counter mis-measured.
const APPLIANCE_PULL_MAX = 12 * 0.0254;

// Bay kinds that sit INSIDE the counter and so testify to its depth. A fridge
// is deliberately absent: it is a tall unit that stands proud of the counter
// by design, and letting a 30" one set the run would push the whole counter
// out past it.
const COUNTER_HEIGHT_BAY_KINDS = new Set([
  'dishwasher',
  'range',
  'cooktop',
  'sink-base',
]);

/**
 * The depth a run must be AT LEAST, given the appliances built into it.
 *
 * RoomPlan under-measures cabinet depth — it reads the door face, and its own
 * beautify pass shrinks further — so a scanned run routinely comes back 3-5"
 * shallow. An appliance housed in that run is better evidence than the boxes
 * around it: a dishwasher is manufactured to fit a standard cabinet, and one
 * cannot be standing in a 19" counter.
 *
 * The floor is the STANDARD depth, never the appliance's own. A slide-in range
 * measures 25-27" over its handles and a fridge 30"+, and both stand proud of
 * the counter in a real kitchen — reading their depth literally would push the
 * whole run out to meet them. What the appliance proves is "this is a standard
 * run", not "this run is as deep as me".
 *
 * Capped at `APPLIANCE_PULL_MAX` (12", user ruling) so a genuinely shallow run
 * with one oddly-placed appliance anchor is not dragged the whole way.
 *
 * NOT for a VANITY, and the reason is the argument above: it rests on the
 * appliance being manufactured to a standard, which is true of a dishwasher
 * and false of a bathroom basin — a bowl is chosen to fit the cabinet, not the
 * other way round, and vanities are routinely 18-21" deep. `sink-base` is in
 * `COUNTER_HEIGHT_BAY_KINDS`, so without this gate a vanity measured at 18.2"
 * was pushed out to the kitchen's 24" for having a sink in it (measured: the
 * real `F6B82D01` vanity, 18.2" -> 24.0"). Masked today only because every
 * derived vanity run stores its measured depth, which outranks this — one
 * cabinet with no `dimensions.z` is all it would take.
 */
function applianceDepthFloor(run, measured) {
  if (!run || measured >= STANDARD_CABINET_DEPTH) return 0;
  if (profileOf(run).id === VANITY_PROFILE_ID) return 0;
  const housed = (run.bays || []).some(
    (b) => b && !b.unplaced && COUNTER_HEIGHT_BAY_KINDS.has(b.kind)
  );
  if (!housed) return 0;
  if (STANDARD_CABINET_DEPTH - measured > APPLIANCE_PULL_MAX) return 0;
  return STANDARD_CABINET_DEPTH;
}

/**
 * The CORNER BOX at an inside corner of this run — the square where the two
 * legs overlap, which is also each leg's perpendicular depth.
 *
 * It is the run's own box depth, never the engine's standard 24". A corner is
 * always an interior vertex of ONE run (`eachCabinetCorner` walks consecutive
 * segments), so both legs share a single depth and the box stays square.
 *
 * Reading the standard here is what made a scanned run step at its own corner:
 * `buildCornerUnits` built the L at 24" while the straight bays either side of
 * it were built at `ctx.cabinetDepth`, so a 15.75" run (a real case, pinned by
 * a unit test in `runCabinetDepth`) got an 8" jog in both the boxes and the
 * countertop.
 */
function cornerBoxFor(ctx) {
  const d = ctx?.cabinetDepth;
  return typeof d === 'number' && d > 0 ? d : STANDARD_CABINET_DEPTH;
}

/**
 * A body taller than any bay can be — a pantry, a tall filler, an oven column
 * section.
 *
 * The threshold is `BASE_HEIGHT + BASE_HEIGHT_TOLERANCE` on purpose: it is the
 * engine's own `TALL_NEIGHBOUR_MIN_HEIGHT` (`fillRun.js`), the line above which
 * the fill reads a body as tall and hangs no upper over it. Sharing it is what
 * makes raising one of these to the upper line safe — anything this catches was
 * already too tall to carry an upper, so nothing can be raised into one.
 */
function isTallBody(o) {
  const h = o?.dimensions?.y;
  return typeof h === 'number' && h > BASE_HEIGHT + BASE_HEIGHT_TOLERANCE;
}

module.exports = {
  BASE_HEIGHT,
  isTallBody,
  cornerBoxFor,
  STANDARD_CABINET_DEPTH,
  BASE_HEIGHT_TOLERANCE,
  isBaseHeight,
  measuredCabinetDepth,
  runCabinetDepth,
};
